import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, CalendarRange, CheckCircle2, Package, Search, Tag, ToggleLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Modal from '@/components/ui/modal'
import { Input } from '@/components/ui/input'
import type { Product } from '@/lib/api/products'
import type { PromotionResponse } from '@/services/promotionsApi'
import {
  formatPromotionDate,
  getPromotionDiscountLabel,
  getPromotionLifecycleBadge,
  getPromotionLifecycleDescription,
  getPromotionTargetLabel,
} from './promotionsUx'

type DiscountMode = 'percent' | 'amount'

export type PromotionFormPayload = {
  name: string
  description: string | null
  discountPct: number | null
  discountAmt: number | null
  productId: string | null
  validFrom: string
  validUntil: string
  active: boolean
}

type PromotionFormValues = {
  name: string
  description: string
  discountMode: DiscountMode
  discountValue: string
  productId: string
  validFrom: string
  validUntil: string
  active: boolean
}

type PromotionFormModalProps = {
  isOpen: boolean
  mode: 'create' | 'edit'
  promotion: PromotionResponse | null
  defaultProductId?: string | null
  products: Product[]
  isSaving: boolean
  onClose: () => void
  onSubmit: (payload: PromotionFormPayload) => Promise<void>
}

type FieldName = keyof PromotionFormValues

type FieldErrors = Partial<Record<FieldName, string>>

const QUICK_VALIDITY = [
  { label: '7 días', days: 7 },
  { label: '15 días', days: 15 },
  { label: '30 días', days: 30 },
]

const QUICK_DESCRIPTIONS = [
  'Promo por tiempo limitado.',
  'Descuento especial para cierre rápido.',
  'Campaña comercial destacada.',
  'Promoción exclusiva por WhatsApp.',
]

function toDateTimeLocal(value?: string | null) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const offset = date.getTimezoneOffset()
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16)
}

function fromDateTimeLocal(value: string) {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString()
}

function createInitialValues(promotion: PromotionResponse | null, defaultProductId?: string | null): PromotionFormValues {
  return {
    name: promotion?.name ?? '',
    description: promotion?.description ?? '',
    discountMode: promotion?.discountPct !== null && promotion?.discountPct !== undefined ? 'percent' : 'amount',
    discountValue:
      promotion?.discountPct !== null && promotion?.discountPct !== undefined
        ? String(promotion.discountPct)
        : promotion?.discountAmt !== null && promotion?.discountAmt !== undefined
          ? String(promotion.discountAmt)
          : '',
    productId: promotion?.productId ?? defaultProductId ?? '',
    validFrom: toDateTimeLocal(promotion?.validFrom),
    validUntil: toDateTimeLocal(promotion?.validUntil),
    active: promotion?.active ?? true,
  }
}

function validatePromotionForm(values: PromotionFormValues): FieldErrors {
  const errors: FieldErrors = {}

  if (!values.name.trim()) {
    errors.name = 'Ingresa un nombre para la promoción.'
  }

  if (!values.discountValue.trim()) {
    errors.discountValue = 'Define el valor del descuento.'
  } else {
    const numericValue = Number(values.discountValue)
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      errors.discountValue = 'El valor debe ser mayor que 0.'
    } else if (values.discountMode === 'percent' && numericValue > 100) {
      errors.discountValue = 'El porcentaje no puede superar 100.'
    }
  }

  if (!values.validFrom) {
    errors.validFrom = 'Define la fecha de inicio.'
  }

  if (!values.validUntil) {
    errors.validUntil = 'Define la fecha de fin.'
  }

  if (values.validFrom && values.validUntil) {
    const start = new Date(values.validFrom)
    const end = new Date(values.validUntil)

    if (Number.isNaN(start.getTime())) {
      errors.validFrom = 'La fecha de inicio no es válida.'
    }

    if (Number.isNaN(end.getTime())) {
      errors.validUntil = 'La fecha de fin no es válida.'
    }

    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end <= start) {
      errors.validUntil = 'La fecha de fin debe ser posterior a la de inicio.'
    }
  }

  return errors
}

function getDiscountInputLabel(mode: DiscountMode) {
  return mode === 'percent' ? 'Porcentaje de descuento' : 'Monto de descuento'
}

function getDiscountInputPlaceholder(mode: DiscountMode) {
  return mode === 'percent' ? 'Ej. 15' : 'Ej. 5000'
}

function getQuickValidityValue(days: number) {
  const now = new Date()
  const next = new Date(now.getTime() + days * 24 * 60 * 60 * 1000)
  return next.toISOString().slice(0, 16)
}

function formatProductPrice(product: Product) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: product.currency,
    maximumFractionDigits: 0,
  }).format(product.priceCents / 100)
}

export function PromotionFormModal({
  isOpen,
  mode,
  promotion,
  defaultProductId,
  products,
  isSaving,
  onClose,
  onSubmit,
}: PromotionFormModalProps) {
  const [values, setValues] = useState<PromotionFormValues>(() => createInitialValues(promotion, defaultProductId))
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({})
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [productSearch, setProductSearch] = useState('')

  useEffect(() => {
    if (!isOpen) return

    setValues(createInitialValues(promotion, defaultProductId))
    setTouched({})
    setSubmitAttempted(false)
    const selected = products.find((product) => product.id === (promotion?.productId ?? defaultProductId ?? ''))
    setProductSearch(selected?.name ?? '')
  }, [isOpen, promotion?.id, defaultProductId, products])

  const fieldErrors = useMemo(() => validatePromotionForm(values), [values])
  const selectedProduct = useMemo(
    () => products.find((product) => product.id === values.productId) ?? null,
    [products, values.productId]
  )

  const filteredProducts = useMemo(() => {
    const term = productSearch.trim().toLowerCase()
    if (!term) {
      return products.slice(0, 8)
    }

    return products.filter((product) => {
      return [product.name, product.sku, product.description, product.category]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term))
    }).slice(0, 8)
  }, [products, productSearch])

  const pricingPreview = useMemo(() => {
    if (!selectedProduct) return null
    const originalPrice = selectedProduct.priceCents / 100
    const val = Number(values.discountValue)
    if (!Number.isFinite(val) || val <= 0) return null

    let savings = 0
    let finalPrice = originalPrice

    if (values.discountMode === 'percent') {
      const pct = Math.min(100, val)
      savings = (originalPrice * pct) / 100
      finalPrice = Math.max(0, originalPrice - savings)
    } else {
      savings = Math.min(originalPrice, val)
      finalPrice = Math.max(0, originalPrice - savings)
    }

    const formatCurrency = (amount: number) =>
      new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: selectedProduct.currency,
        maximumFractionDigits: 0,
      }).format(amount)

    return {
      originalFormatted: formatCurrency(originalPrice),
      finalFormatted: formatCurrency(finalPrice),
      savingsFormatted: formatCurrency(savings),
      isExcessive: values.discountMode === 'amount' && val > originalPrice,
    }
  }, [selectedProduct, values.discountMode, values.discountValue])

  const showError = (field: FieldName) => Boolean(submitAttempted || touched[field])

  const markTouched = (field: FieldName) => {
    setTouched((current) => ({ ...current, [field]: true }))
  }

  const canSubmit = Object.keys(fieldErrors).length === 0 && Boolean(values.name.trim()) && Boolean(values.discountValue.trim())

  const handleQuickValidity = (days: number) => {
    const nextUntil = getQuickValidityValue(days)
    setValues((current) => ({ ...current, validUntil: nextUntil }))
    markTouched('validUntil')
  }

  const handleSubmit = async () => {
    setSubmitAttempted(true)

    if (Object.keys(fieldErrors).length > 0) {
      return
    }

    const numericValue = Math.max(1, Math.round(Number(values.discountValue)))
    const payload: PromotionFormPayload = {
      name: values.name.trim(),
      description: values.description.trim() ? values.description.trim() : null,
      discountPct: values.discountMode === 'percent' ? numericValue : null,
      discountAmt: values.discountMode === 'amount' ? numericValue : null,
      productId: values.productId.trim() ? values.productId.trim() : null,
      validFrom: fromDateTimeLocal(values.validFrom),
      validUntil: fromDateTimeLocal(values.validUntil),
      active: values.active,
    }

    await onSubmit(payload)
    onClose()
  }

  const currentLifecycle = promotion ? getPromotionLifecycleBadge(promotion.lifecycleStatus) : null
  const summaryDiscountLabel =
    values.discountValue.trim() && Number.isFinite(Number(values.discountValue))
      ? `${values.discountMode === 'percent' ? `${values.discountValue}%` : `Monto fijo ${values.discountValue}`}`
      : 'Sin definir'

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={mode === 'edit' ? 'Editar promoción' : 'Nueva promoción'} size="xl">
      <div className="space-y-5">
        <div className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-3">
          <div className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-100">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Descuento</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">{summaryDiscountLabel}</p>
          </div>
          <div className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-100">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Destino</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">{getPromotionTargetLabel(selectedProduct?.name)}</p>
          </div>
          <div className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-100">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Estado</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">{values.active ? 'Activa' : 'Inactiva'}</p>
          </div>
        </div>

        {currentLifecycle ? (
          <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
              {currentLifecycle.label}
            </span>
            <span>{promotion ? getPromotionLifecycleDescription(promotion.lifecycleStatus) : 'Lista para publicar'}</span>
          </div>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4">
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2 text-slate-900">
                <Tag className="h-4 w-4 text-[#075f55]" />
                <h3 className="text-base font-semibold">Datos generales</h3>
              </div>

              <div className="mt-4 space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Nombre</label>
                  <Input
                    value={values.name}
                    onChange={(e) => {
                      setValues((current) => ({ ...current, name: e.target.value }))
                      markTouched('name')
                    }}
                    onBlur={() => markTouched('name')}
                    placeholder="Promo de lanzamiento"
                  />
                  {showError('name') && fieldErrors.name ? <p className="mt-1 text-xs text-rose-600">{fieldErrors.name}</p> : null}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Descripción</label>
                  <textarea
                    value={values.description}
                    onChange={(e) => {
                      setValues((current) => ({ ...current, description: e.target.value }))
                      markTouched('description')
                    }}
                    onBlur={() => markTouched('description')}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20"
                    placeholder="Texto breve para contexto comercial"
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Tipo de descuento</label>
                    <select
                      value={values.discountMode}
                      onChange={(e) => {
                        setValues((current) => ({ ...current, discountMode: e.target.value as DiscountMode }))
                        markTouched('discountValue')
                      }}
                      className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm outline-none transition-colors focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20"
                    >
                      <option value="percent">Porcentaje</option>
                      <option value="amount">Monto fijo</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">{getDiscountInputLabel(values.discountMode)}</label>
                    <Input
                      type="number"
                      min={1}
                      max={values.discountMode === 'percent' ? 100 : undefined}
                      value={values.discountValue}
                      onChange={(e) => {
                        setValues((current) => ({ ...current, discountValue: e.target.value }))
                        markTouched('discountValue')
                      }}
                      onBlur={() => markTouched('discountValue')}
                      placeholder={getDiscountInputPlaceholder(values.discountMode)}
                    />
                    {showError('discountValue') && fieldErrors.discountValue ? (
                      <p className="mt-1 text-xs text-rose-600">{fieldErrors.discountValue}</p>
                    ) : null}
                  </div>
                </div>

                {pricingPreview && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-3 text-xs flex items-center justify-between gap-2">
                    <div>
                      <span className="text-slate-400 line-through mr-1.5">{pricingPreview.originalFormatted}</span>
                      <span className="font-bold text-[#075E54] text-sm">{pricingPreview.finalFormatted}</span>
                      <span className="text-slate-500 ml-1.5 font-medium">precio final</span>
                    </div>
                    <span className="rounded-full bg-emerald-100 text-[#075E54] font-bold px-2.5 py-0.5 text-[11px] border border-emerald-200">
                      Ahorro cliente: {pricingPreview.savingsFormatted}
                    </span>
                  </div>
                )}
                {pricingPreview?.isExcessive && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 font-medium">
                    ⚠️ El descuento de monto fijo supera el precio original del producto ({pricingPreview.originalFormatted}). El precio final será 0.
                  </p>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Vigencia inicial</label>
                    <Input
                      type="datetime-local"
                      value={values.validFrom}
                      onChange={(e) => {
                        setValues((current) => ({ ...current, validFrom: e.target.value }))
                        markTouched('validFrom')
                      }}
                      onBlur={() => markTouched('validFrom')}
                    />
                    {showError('validFrom') && fieldErrors.validFrom ? <p className="mt-1 text-xs text-rose-600">{fieldErrors.validFrom}</p> : null}
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Vigencia final</label>
                    <Input
                      type="datetime-local"
                      value={values.validUntil}
                      onChange={(e) => {
                        setValues((current) => ({ ...current, validUntil: e.target.value }))
                        markTouched('validUntil')
                      }}
                      onBlur={() => markTouched('validUntil')}
                    />
                    {showError('validUntil') && fieldErrors.validUntil ? <p className="mt-1 text-xs text-rose-600">{fieldErrors.validUntil}</p> : null}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {QUICK_VALIDITY.map((quick) => (
                    <button
                      key={quick.days}
                      type="button"
                      onClick={() => handleQuickValidity(quick.days)}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100"
                    >
                      {quick.label}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2 text-slate-900">
                <Package className="h-4 w-4 text-[#075f55]" />
                <h3 className="text-base font-semibold">Producto objetivo</h3>
              </div>

              <div className="mt-4 space-y-4">
                {selectedProduct ? (
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-emerald-900">{selectedProduct.name}</p>
                        <p className="text-xs text-emerald-700">SKU {selectedProduct.sku} · Stock {selectedProduct.stock}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setValues((current) => ({ ...current, productId: '' }))
                          setProductSearch('')
                        }}
                        className="rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
                      >
                        Limpiar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                    Esta promoción aplica a toda la tienda hasta que selecciones un producto.
                  </div>
                )}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Buscar producto</label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      className="pl-9"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Nombre, SKU, categoría o descripción"
                    />
                  </div>
                </div>

                <div className="grid gap-3">
                  {filteredProducts.map((product) => {
                    const isSelected = values.productId === product.id
                    return (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => {
                          setValues((current) => ({ ...current, productId: product.id }))
                          setProductSearch(product.name)
                          markTouched('productId')
                        }}
                        className={[
                          'flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors',
                          isSelected ? 'border-[#075f55]/30 bg-[#075f55]/5' : 'border-slate-200 bg-slate-50 hover:border-[#075f55]/20 hover:bg-white',
                        ].join(' ')}
                      >
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
                          {product.imageUrl ? (
                            <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
                          ) : (
                            <Package className="h-5 w-5 text-slate-400" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-slate-900">{product.name}</p>
                          <p className="truncate text-xs text-slate-500">
                            SKU {product.sku} · Stock {product.stock}
                            {product.category ? ` · ${product.category}` : ''}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-semibold text-slate-500">Precio</p>
                          <p className="text-sm font-bold text-slate-900">{formatProductPrice(product)}</p>
                        </div>
                      </button>
                    )
                  })}

                  {filteredProducts.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                      No encontramos productos con ese criterio.
                    </div>
                  ) : null}
                </div>
              </div>
            </section>
          </div>

          <div className="space-y-4">
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2 text-slate-900">
                <ToggleLeft className="h-4 w-4 text-[#075f55]" />
                <h3 className="text-base font-semibold">Estado y publicación</h3>
              </div>

              <div className="mt-4 space-y-3">
                <button
                  type="button"
                  onClick={() => setValues((current) => ({ ...current, active: !current.active }))}
                  className={[
                    'flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition-colors',
                    values.active ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-slate-50 text-slate-600',
                  ].join(' ')}
                >
                  <span className="text-sm font-semibold">{values.active ? 'Activa' : 'Inactiva'}</span>
                  <span className="text-xs font-medium">{values.active ? 'Visible para ventas' : 'No se aplicará al funnel'}</span>
                </button>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    Resumen
                  </div>
                  <div className="mt-3 space-y-2 text-sm text-slate-600">
                    <p>
                      <span className="font-semibold text-slate-900">Descuento:</span> {getPromotionDiscountLabel({
                        discountPct: values.discountMode === 'percent' && values.discountValue.trim() ? Number(values.discountValue) : null,
                        discountAmt: values.discountMode === 'amount' && values.discountValue.trim() ? Number(values.discountValue) : null,
                      })}
                    </p>
                    <p>
                      <span className="font-semibold text-slate-900">Destino:</span> {getPromotionTargetLabel(selectedProduct?.name)}
                    </p>
                    <p>
                      <span className="font-semibold text-slate-900">Vigencia:</span> {values.validFrom && values.validUntil ? `${formatPromotionDate(fromDateTimeLocal(values.validFrom))} → ${formatPromotionDate(fromDateTimeLocal(values.validUntil))}` : 'Pendiente'}
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Sugerencias rápidas</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {QUICK_DESCRIPTIONS.map((template) => (
                      <button
                        key={template}
                        type="button"
                        onClick={() => {
                          setValues((current) => ({ ...current, description: template }))
                          markTouched('description')
                        }}
                        className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100"
                      >
                        {template}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2 text-slate-900">
                <CalendarRange className="h-4 w-4 text-[#075f55]" />
                <h3 className="text-base font-semibold">Resumen final</h3>
              </div>

              <div className="mt-4 space-y-3 text-sm text-slate-600">
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Nombre</p>
                  <p className="mt-1 font-semibold text-slate-900">{values.name || 'Sin definir'}</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Estado</p>
                  <p className="mt-1 font-semibold text-slate-900">{values.active ? 'Activa' : 'Inactiva'}</p>
                </div>
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Vigencia</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {values.validFrom && values.validUntil ? `${formatPromotionDate(fromDateTimeLocal(values.validFrom))} → ${formatPromotionDate(fromDateTimeLocal(values.validUntil))}` : 'Pendiente'}
                  </p>
                </div>
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Destino</p>
                  <p className="mt-1 font-semibold text-slate-900">{getPromotionTargetLabel(selectedProduct?.name)}</p>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-200 pt-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <AlertCircle className="h-4 w-4" />
            Los cambios se validan antes de guardar y la promoción queda aislada por tenant.
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              Cancelar
            </Button>
            <Button
              type="button"
              className="bg-[#075f55] px-5 py-3 text-white hover:bg-[#064d46]"
              onClick={handleSubmit}
              disabled={isSaving || !canSubmit}
            >
              {isSaving ? 'Guardando...' : mode === 'edit' ? 'Guardar cambios' : 'Crear promoción'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export { validatePromotionForm, createInitialValues, fromDateTimeLocal, toDateTimeLocal }
export default PromotionFormModal
